import { RouteObject } from "react-router-dom";
import {
  LoginPage,
  MfaVerificationPage,
  RequestPasswordResetPage,
  ResetPasswordPage,
  ResetPasswordSentPage,
  ForcedPasswordResetPage,
  UnauthorizedPage,
} from "../pages/auth";
import HomePage from "../pages/HomePage";
import ProfilePage from "../pages/ProfilePage";
import VerifyEmailPage from "../pages/VerifyEmailPage";
import NotFoundPage from "../pages/NotFoundPage";
import Organizations from "../pages/admin/Organizations";
import {
  UserListPage,
  CreateUserPage,
  EditUserPage,
  UserDetailsPage,
  BulkImportPage,
} from "../pages/users";
import { AdminDashboardPage, EmailTestingPage } from "../pages/admin";
import OrgStrategyPage from "../pages/admin/OrgStrategyPage";
import StrategiesPage from "../pages/admin/StrategiesPage";
import {
  DocumentListPage,
  DocumentUploadPage,
  DocumentDetailsPage,
} from "../pages/documents";
import ChatPage from "../pages/ChatPage";
import { SearchPage } from "../pages/SearchPage";
import { AnalyticsLayout } from "../pages/analytics/AnalyticsLayout";
import { UsagePage } from "../pages/analytics/UsagePage";
import { QueryLogsPage } from "../pages/analytics/QueryLogsPage";
import { DocumentPerformancePage } from "../pages/analytics/DocumentPerformancePage";
import { GapAnalysisPage } from "../pages/analytics/GapAnalysisPage";
import { AuditLogPage } from "../pages/analytics/AuditLogPage";
import { SettingsLayout } from "../pages/settings/SettingsLayout";
import { ApiDocsPage } from "../pages/settings/ApiDocsPage";
import { AiKeysPage } from "../pages/settings/AiKeysPage";
import { ApiKeysPage } from "../pages/settings/ApiKeysPage";
import { OrganisationPage } from "../pages/settings/OrganisationPage";
import { ExtractionSettingsPage } from "../pages/settings/ExtractionSettingsPage";
import { TeamsBindingsPage } from "../pages/settings/TeamsBindingsPage";
import ProtectedRoute from "./components/ProtectedRoute";
import Layout from "../components/Layout/Layout";
import AdminLayout from "../components/Layout/AdminLayout";
import { Navigate } from "react-router-dom";
// import { UserRoleEnum } from '@shared/schemas'; // Commented out as it's not used

const routes: RouteObject[] = [
  // Admin routes with AdminLayout (SuperAdmin only)
  {
    path: "/admin",
    element: (
      <ProtectedRoute requiredRoles={["SUPER_ADMIN"]}>
        <AdminLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <AdminDashboardPage /> },
      { path: "organizations", element: <Organizations /> },
      { path: "email-testing", element: <EmailTestingPage /> },
      { path: "strategies", element: <StrategiesPage /> },
      {
        path: "organizations/:orgId/strategy",
        element: <OrgStrategyPage />,
      },
      { path: "profile", element: <ProfilePage /> },
    ],
  },

  // Shared routes (any authenticated user, no org required)
  {
    path: "/profile",
    element: (
      <ProtectedRoute>
        <Layout />
      </ProtectedRoute>
    ),
    children: [{ index: true, element: <ProfilePage /> }],
  },

  // Org-scoped routes with Layout (requires org membership)
  {
    path: "/",
    element: (
      <ProtectedRoute requiresOrg>
        <Layout />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <HomePage />,
      },
      // Settings routes
      {
        path: "settings",
        element: <SettingsLayout />,
        children: [
          { index: true, element: <Navigate to="organisation" replace /> },
          { path: "organisation", element: <OrganisationPage /> },
          { path: "api-docs", element: <ApiDocsPage /> },
          { path: "ai-keys", element: <AiKeysPage /> },
          { path: "api-keys", element: <ApiKeysPage /> },
          { path: "extraction", element: <ExtractionSettingsPage /> },
          { path: "teams", element: <TeamsBindingsPage /> },
        ],
      },
      // Document management routes
      {
        path: "documents",
        element: <DocumentListPage />,
      },
      {
        path: "documents/upload",
        element: <DocumentUploadPage />,
      },
      {
        path: "documents/:id",
        element: <DocumentDetailsPage />,
      },
      // Search route
      {
        path: "search",
        element: <SearchPage />,
      },
      // Chat route
      {
        path: "chat",
        element: <ChatPage />,
      },
      // Analytics routes
      {
        path: "analytics",
        element: <AnalyticsLayout />,
        children: [
          { index: true, element: <Navigate to="usage" replace /> },
          { path: "usage", element: <UsagePage /> },
          { path: "query-logs", element: <QueryLogsPage /> },
          {
            path: "document-performance",
            element: <DocumentPerformancePage />,
          },
          { path: "gap-analysis", element: <GapAnalysisPage /> },
          { path: "audit-log", element: <AuditLogPage /> },
        ],
      },
      // User management routes - ADMIN and OWNER roles
      {
        path: "users",
        element: (
          <ProtectedRoute requiredRoles={["ADMIN", "OWNER"]}>
            <UserListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "users/create",
        element: (
          <ProtectedRoute requiredRoles={["ADMIN", "OWNER"]}>
            <CreateUserPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "users/import",
        element: (
          <ProtectedRoute requiredRoles={["ADMIN", "OWNER"]}>
            <BulkImportPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "users/:id",
        element: (
          <ProtectedRoute requiredRoles={["ADMIN", "OWNER"]}>
            <UserDetailsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "users/edit/:id",
        element: (
          <ProtectedRoute requiredRoles={["ADMIN", "OWNER"]}>
            <EditUserPage />
          </ProtectedRoute>
        ),
      },
    ],
  },

  // Auth routes
  {
    path: "/auth/login",
    element: <LoginPage />,
  },
  {
    path: "/auth/mfa",
    element: <MfaVerificationPage />,
  },
  {
    path: "/auth/reset-password-request",
    element: <RequestPasswordResetPage />,
  },
  {
    path: "/auth/reset-password/:token",
    element: <ResetPasswordPage />,
  },
  {
    path: "/auth/reset-password-sent",
    element: <ResetPasswordSentPage />,
  },
  {
    path: "/auth/forced-password-reset",
    element: <ForcedPasswordResetPage />,
  },

  // Email verification route (public)
  {
    path: "/verify-email",
    element: <VerifyEmailPage />,
  },

  // Unauthorized page
  {
    path: "/unauthorized",
    element: <UnauthorizedPage />,
  },

  // Not found - 404 page
  {
    path: "*",
    element: <NotFoundPage />,
  },
];

export default routes;
